import { useMutation, useQuery } from '@apollo/client';
import DocsIcon from '@clearkit/icons/glyphs/Docs';
import { CKBox, CKButton, CKField, CKTextField } from '@clearkit/react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useEffect, useState } from 'react';

import SecurityDisclaimer from '~/components/ConfigurationSecurityDisclaimer';

import SalesforceConnectionForm from '../SalesforceConnectionForm';
import query from './query.graphql';
import mutation from './update.graphql';

export default function PardotConnectionForm({ auth, onSuccess }) {
  const [pardotBusinessUnitId, setPardotBusinessUnitId] = useState(null);
  const { data = {}, loading, refetch } = useQuery(query);
  const [updatePardotID, { loading: updatePardotLoading }] = useMutation(
    mutation,
  );
  const [updateErrors, setUpdateErrors] = useState({});

  const { accountConnector = null } = data;

  useEffect(() => {
    if (accountConnector) {
      setPardotBusinessUnitId(accountConnector.pardotBusinessUnitId);
    }
  }, [data]);

  async function updatePardotBusinessUnit() {
    setUpdateErrors({});

    const { data: mutationData } = await updatePardotID({
      variables: {
        input: {
          id: accountConnector.id,
          pardotBusinessUnitId,
        },
      },
    });

    let { errors } = mutationData.updatePardotBusinessUnitId;

    if (errors.length > 0) {
      const errorObj = {};

      errors.forEach(({ field, messages }) => {
        errorObj[field] = messages;
      });

      setUpdateErrors(errorObj);
    } else {
      onSuccess();
    }
  }

  if (loading) {
    return <CircularProgress />;
  }

  if (!accountConnector) {
    return (
      <>
        <SalesforceConnectionForm
          onAuth={(salesforceService) => auth(salesforceService, refetch)}
        />
        <SecurityDisclaimer className="pt-4" />
      </>
    );
  }

  return (
    <div>
      <CKBox className="p-8 sm:w-xl">
        <div className="text-lg font-semibold text-center text-black">
          Connect to a Pardot Account
        </div>

        <p className="pb-4 text-center text-gray-500 text-md">
          Connect your Pardot account to sync Clearbit data to Pardot.
        </p>

        <div className="flex flex-col pb-4 gap-2">
          <div className="flex justify-between text-sm">
            <label htmlFor="pardot-business-unit-id">Business Unit ID</label>
            <a
              href="https://help.clearbit.com/hc/en-us/articles/4420193089047"
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-1">
              <DocsIcon />
              <span>How to find your Business Unit ID key</span>
            </a>
          </div>

          <CKField
            variant={updateErrors.pardot_business_unit_id ? 'error' : null}>
            <CKTextField
              className="block w-full"
              id="pardot-business-unit-id"
              value={pardotBusinessUnitId || ''}
              onChange={(e) => {
                const value = e.currentTarget.value.trim();
                setPardotBusinessUnitId(value);
              }}
            />
          </CKField>

          <div className="text-red-500">
            {updateErrors.pardot_business_unit_id}
          </div>
        </div>

        <CKButton
          onClick={updatePardotBusinessUnit}
          disabled={!pardotBusinessUnitId}
          isLoading={updatePardotLoading}
          variant={CKButton.Variant.bold}
          variantColor={CKButton.VariantColor.blue}>
          Connect Pardot
        </CKButton>

        <div className="text-red-500">{updateErrors.base}</div>
      </CKBox>
      <SecurityDisclaimer className="pt-4" />
    </div>
  );
}
